<script lang="ts">
  import DateTimeContent from "$lib/content/components/DateTimeContent.svelte";
  import type { LayoutProps } from "$lib/layouts/layoutTypes";
  import { t } from "$lib/utils/i18n";
  import { getConfigurationContext } from "$lib/elev8Api/configurationContext.svelte";

  import qrCodeSvgUrl from "$assets/vc-privacy-qr-code.svg";
  import elev8LogoPngUrl from "$assets/elev8-by-vertical-city-logo-full-colour-highres.png";
  import vcLogoPngUrl from "$assets/vc-logo.png";

  const {
    logoUrl = elev8LogoPngUrl,
    fullBleedLogo = false,
    visionEnabled = false,
    ContentMain,
    ContentSecondary,
    ContentTertiary,
    ContentWeather,
  }: LayoutProps = $props();
  const config = getConfigurationContext();
</script>

<div class="layout-wrapper" data-layout="weather-bottom">
  <div class="sidebar">
    <div class="sidebar-top">
      <div
        class="sidebar-top__logo"
        class:sidebar-top__logo--full-bleed={fullBleedLogo}
      >
        <img
          class="sidebar-top__logo-img"
          class:sidebar-top__logo-img--full-bleed={fullBleedLogo}
          src={logoUrl}
          alt={t("alt.propertyLogo", config.language)}
          crossorigin="anonymous"
        />
      </div>
      <div class="sidebar__content">
        {@render ContentSecondary?.()}
      </div>
    </div>
    <div class="sidebar-bottom">
      {#if visionEnabled}
        <div class="sidebar-bottom__privacy">
          <div class="sidebar-bottom__privacy-text">
            {t("privacy.notice", config.language)}
          </div>
          <img
            class="sidebar-bottom__privacy-qrcode"
            src={qrCodeSvgUrl}
            alt={t("alt.privacyQr", config.language)}
          />
        </div>
      {:else}
        <img
          src={vcLogoPngUrl}
          class="sidebar-bottom__logo-img"
          alt={t("alt.verticalCityLogo", config.language)}
        />
      {/if}
    </div>
  </div>
  <!-- end sidebar -->
  <div class="main">
    <div class="main__content">
      {@render ContentMain?.()}
    </div>
    <div class="main-bottom">
      <div class="main-bottom__content">
        {@render ContentTertiary?.()}
        {@render ContentWeather?.()}
      </div>
      <div class="main-bottom__time">
        <DateTimeContent />
      </div>
    </div>
  </div>
</div>

<style>
  :root {
    --sidebar-width-flex: 1; /* 1/5 of width */
    --main-width-flex: 4; /* 4/5 of width */
    --main-top-flex: 4; /* 4/5 of height */
    --main-bottom-flex: 1; /* 1/5 of height */
  }

  /* @media screen and (max-width: 768px) {*/

  .layout-wrapper {
    --layout-font-scale: 0.8;
    height: 100%;
    width: 100%;
    margin: 0;
    display: flex;
  }

  .sidebar {
    flex: var(--sidebar-width-flex, 1);
    display: flex;
    flex-direction: column;
    background: url("$assets/side_notice_background.png");
    background-size: cover;
    text-align: center;
  }

  .sidebar-top {
    flex: var(--main-top-flex, 4);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .sidebar-top__logo {
    flex: var(--main-bottom-flex, 1);
    background: var(--vc-white);
    border-bottom: 3px solid var(--vc-accent);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  /* Full-bleed: the asset already fills the box edge-to-edge, so the box takes
     its height from the asset instead of the fixed 1/5 slot. */
  .sidebar-top__logo--full-bleed {
    flex: 0 0 auto;
    border-bottom: none;
  }

  .sidebar-top__logo-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    margin: 0.8rem;
  }

  .sidebar-top__logo-img--full-bleed {
    height: auto; /* box is content-sized now; inherited 100% would be circular */
    margin: 0;
  }

  .sidebar__content {
    flex: calc(var(--main-top-flex, 4) - var(--main-bottom-flex, 1));
    overflow: hidden;
  }

  .sidebar-bottom {
    flex: var(--main-bottom-flex, 1);
    background: var(--vc-white);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .sidebar-bottom__privacy {
    display: flex;
    align-items: center;
    height: 100%;
    background-color: var(--vc-white);
    padding: 1rem 1.85rem;
  }

  .sidebar-bottom__privacy-text {
    flex: 4;
    text-align: left;
    font-size: 0.75rem;
    line-height: 1;
  }

  .sidebar-bottom__privacy-qrcode {
    flex: 3;
    margin-left: 0.5em;
  }

  .sidebar-bottom__logo-img {
    width: 66%;
    height: 100%;
    object-fit: contain;
  }

  .main {
    flex: var(--main-width-flex, 4);
    display: flex;
    flex-direction: column;
    background-size: cover;
    background-position: center;
  }

  .main__content {
    flex: var(--main-top-flex, 4);
    background-color: white;
    overflow: hidden;
  }

  .main-bottom {
    flex: var(--main-bottom-flex, 1);
    display: flex;
    background: white;
  }

  .main-bottom__content {
    flex: 1;
    color: var(--vc-primary);
  }

  .main-bottom__time {
    flex: 0 0 25%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    background-color: var(--vc-white);
    border-top: 1px solid var(--vc-neutral-light-20);
    & :global(div) {
      font-family: var(--vc-font-h1);
      font-weight: var(--vc-font-weight-h1);
      font-style: var(--vc-font-style-h1);
      color: var(--vc-primary);
    }
  }

  .main-bottom__time :global(.time) {
    font-size: 4rem;
    line-height: 1.3;
  }

  .main-bottom__time :global(.date) {
    font-size: 2rem;
    font-weight: 400;
  }
</style>
